/** Low, partly buried natural rock; finite native meshes shared by every deposit. */
import type { Graphics } from 'pixi.js';
import { shade } from '../data/palette.ts';
import type { NodeStageId } from '../sim/systems/nodes.ts';

type Point = readonly [number, number];

// A–K form an irregular convex silhouette; P/Q/R form two offset ridges.
// Faces follow those ridges, with no central peak, horizontal slab or extrusion.
const BOULDERS: readonly (readonly Point[])[] = [
  [[18,101],[25,84],[40,72],[63,66],[89,74],[105,89],[111,103],
    [101,114],[78,120],[45,117],[25,112],[59,86],[43,103],[84,94]],
  [[19,102],[25,88],[39,75],[68,70],[92,78],[107,91],[113,105],
    [99,116],[73,120],[43,117],[24,112],[57,91],[39,105],[86,97]],
  [[17,102],[25,86],[47,76],[76,68],[97,80],[108,94],[112,108],
    [94,117],[69,120],[39,117],[22,111],[64,91],[43,105],[86,95]],
];
const FACES = [
  [0,1,2,11,12,10],       // long shoulder facing the upper-left light
  [2,3,4,5,13,11],       // broad, tilted crown, never a level platform
  [10,12,11,13,7,8,9],   // irregular front slope running into the soil
  [4,5,6,7,13],          // narrower shaded eastern face
  [0,10,9],              // a small buried lower edge
] as const;

/**
 * Ground contact centre is (64,112) on the128px canvas; frontmost rock is y120.
 * Full crests rise only46px, so the reduced gameplay canvas stays below a house.
 * Quarrying lowers the same mass and exposes one broken face without a cut yard.
 */
export function drawOutcrop(g: Graphics, ore: boolean, variant: number, stage: NodeStageId): void {
  const v = ((variant % 3) + 3) % 3;
  const worked = stage === 'worked', spent = stage === 'spent', gone = stage === 'gone';
  const height = gone ? .12 : spent ? .32 : worked ? .7 : 1;
  const width = gone ? .58 : spent ? .76 : worked ? .94 : 1;
  const pos = ([x,y]: Point): [number,number] => [64 + (x-64)*width, 112 + (y-112)*height];
  const polygon = (points: readonly Point[], color: number, alpha = 1): void => {
    g.poly(points.flatMap(pos)).fill({color,alpha});
  };
  const line = (points: readonly Point[], color: number, thickness: number, alpha = 1): void => {
    const p = points.map(pos);
    g.moveTo(...p[0]);
    for (let i=1; i<p.length; i++) g.lineTo(...p[i]);
    g.stroke({color,width:thickness,alpha,cap:'round',join:'round'});
  };

  // A soft contact shadow only: the surrounding ground remains visible.
  const shadowWidth = gone ? .62 : spent ? .78 : 1;
  g.poly([19,111,34,105,72,105,110,111,117,117,97,123,57,123,26,119]
    .map((n,i) => i%2 === 0 ? 64+(n-64)*shadowWidth : 112+(n-112)*.72))
    .fill({color:0x20302a,alpha:gone?.09:.18});

  const colors = ore
    ? [0x626e70,0x78827f,0x556466,0x3e5158,0x3b4b4e]
    : [0x8c968b,0xa0a79b,0x7b877c,0x627166,0x59685b];
  const tone = 1 + (v-1)*.025;

  // One attached rough edge projects from the parent mass; it is neither a
  // separate block nor a ring of loose pebbles surrounding the deposit.
  if (v !== 1) {
    polygon([[15,110],[20,103],[33,102],[39,110],[28,117],[17,115]], shade(colors[2],.96));
    polygon([[15,110],[20,103],[33,102],[27,109],[17,113]], shade(colors[0],.96));
  }
  const mesh = BOULDERS[v];
  for (let i=0; i<FACES.length; i++) {
    polygon(FACES[i].map(n=>mesh[n]),shade(colors[i],tone));
  }
  if (v !== 2) {
    polygon([[85,111],[98,107],[108,113],[104,119],[88,120],[80,116]], shade(colors[2],.92));
    polygon([[85,111],[98,107],[108,113],[96,116],[80,116]], shade(colors[0],.93));
  }

  if (!gone) {
    // A short fault changes direction across the shoulder; no masonry seams or
    // repeated horizontal bands. A second fine split ends inside the rock.
    const fault: Point[] = v === 1
      ? [[68,73],[63,81],[65,89],[58,98],[59,104]]
      : v === 2 ? [[75,72],[70,82],[73,90],[67,98],[68,107]]
        : [[64,70],[58,78],[61,86],[55,94],[58,103]];
    if (ore) {
      line(fault,0x925e40,2.8*width,.9);
      line(fault.map(([x,y])=>[x-.45,y] as Point),0x303f43,1.05*width,.95);
      line([[88,88],[84,92],[86,99],[80,105]],0x9e7050,1.8*width,.85);
    } else {
      line(fault,0x4a594d,.85*width,.68);
      line([[31,92],[36,95],[39,101]],0x56644f,.65*width,.5);
      polygon([[35,83],[44,79],[51,84],[47,89],[37,91]],0x869275,.22);
    }
    if (worked || spent) {
      // A fresh, diagonal break replaces a small part of the front shoulder.
      polygon([[62,95],[76,92],[87,101],[82,113],[65,116],[57,108]],
        ore?0x728180:0x9da79a,.85);
      polygon([[76,92],[87,101],[82,113],[78,104]],ore?0x465c62:0x738574);
      if (ore) line([[66,99],[72,104],[68,111]],0x9e6947,1.6*width,.85);
    }
  }
}
